// Thin fetch wrapper + React Query hooks for SQP dashboard.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

async function apiFetch(path, opts = {}) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  // Parse body up-front so error paths can surface the server's actual message
  // (e.g. "Invalid credentials" instead of the generic "Unauthorized" curl-style).
  const text = await res.text();
  let json = {};
  try { json = text ? JSON.parse(text) : {}; } catch (_) { /* leave as {} */ }

  if (res.status === 401) {
    // Auth v2: redirect to /login if we're not already there. When on the
    // login page (or hitting auth endpoints), let the caller see the real
    // error instead of redirect-looping.
    const onLoginPage = window.location.pathname === '/login';
    const isAuthEndpoint = path.startsWith('/api/auth/');
    if (!onLoginPage && !isAuthEndpoint) {
      const next = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(next)}`;
    }
    throw new Error(json.error || 'Unauthorized');
  }
  if (!res.ok || json.ok === false) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

// ─── auth ────────────────────────────────────────────────────────────────
export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiFetch('/api/auth/me'),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, password }) =>
      apiFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => qc.clear(),
  });
}

// ─── queries ─────────────────────────────────────────────────────────────
export function useHealth() {
  return useQuery({ queryKey: ['health'], queryFn: () => apiFetch('/api/health') });
}

export function useOperators(refetchSeconds = 30) {
  return useQuery({
    queryKey: ['operators'],
    queryFn: () => apiFetch('/api/operators'),
    refetchInterval: refetchSeconds * 1000,
  });
}

export function useSystemPm2(refetchSeconds = 15) {
  return useQuery({
    queryKey: ['system', 'pm2'],
    queryFn: () => apiFetch('/api/system/pm2'),
    refetchInterval: refetchSeconds * 1000,
  });
}

// The cr-run DRAIN loops (CR intake pollers) aren't PM2 processes — their health comes from the
// cr-drain-health.sh status JSON. See routes/system.js:/cr-drain-health.
export function useCrDrainHealth(refetchSeconds = 30) {
  return useQuery({
    queryKey: ['system', 'cr-drain-health'],
    queryFn: () => apiFetch('/api/system/cr-drain-health'),
    refetchInterval: refetchSeconds * 1000,
  });
}

export function useSystemLogs(processName, { lines = 50, stream = 'both', enabled = true } = {}) {
  return useQuery({
    queryKey: ['system', 'logs', processName, lines, stream],
    queryFn: () => apiFetch(`/api/system/logs/${encodeURIComponent(processName)}?lines=${lines}&stream=${stream}`),
    enabled: !!processName && enabled,
    refetchInterval: 10_000,
  });
}

// held: 'all' → the server drops the time window + operator scope and returns
// EVERY held-in-practice trip (incl. eas-owned CR intake holds), so nothing ages
// out of view. When held is set the `hours` window is not sent.
export function useTripsToday({ operator, hours = 24, status, phase2Decision, limit = 200, held } = {}) {
  const params = new URLSearchParams();
  if (operator)       params.set('operator', operator);
  if (held)           params.set('held', String(held));
  else if (hours)     params.set('hours', String(hours));
  if (status)         params.set('status', status);
  if (phase2Decision) params.set('phase2_decision', phase2Decision);
  if (limit)          params.set('limit', String(limit));
  return useQuery({
    queryKey: ['trips', 'today', operator, held ? `held:${held}` : `hours:${hours}`, status, phase2Decision, limit],
    queryFn: () => apiFetch(`/api/trips/today?${params.toString()}`),
    refetchInterval: 60_000,
  });
}

// Centralized-Request held backlog — the host-intake CRs (operator_id='eas',
// do_not_post=1) that /api/trips/today can't surface. status='held' (default)
// returns the parked holds; status='all' returns every CR-source row. The
// server applies a wide created_at window (default 90d) so the backlog isn't
// truncated.
export function useCentralizedRequests({ status = 'held', days, limit = 500 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (days)   params.set('days', String(days));
  if (limit)  params.set('limit', String(limit));
  return useQuery({
    queryKey: ['centralized-requests', status, days || null, limit],
    queryFn: () => apiFetch(`/api/centralized-requests?${params.toString()}`),
    refetchInterval: 60_000,
  });
}

// CR processing gates — runtime toggles for the Centralized-Request pipeline.
//   cr_save_enabled       — global host-intake "Save CR" gate
//   cr_quote_enabled:<op> — per-operator "Quote CR" gate
// Independent of each other (Save can be ON while an operator's Quote is OFF).
export function useCrGates() {
  return useQuery({
    queryKey: ['cr-gates'],
    queryFn: () => apiFetch('/api/centralized-requests/gates'),
    refetchInterval: 30_000,
  });
}

export function useSetCrGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ gate, operator, enabled }) =>
      apiFetch('/api/centralized-requests/gates', {
        method: 'POST',
        body: JSON.stringify({ gate, operator, enabled }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cr-gates'] });
      qc.invalidateQueries({ queryKey: ['centralized-requests'] });
    },
  });
}

// TRS (Trip Request Solver) verdict-review surface. The crew-positioning solver runs in SHADOW and
// logs a SEND/HOLD/ALREADY_LEGAL/RECHECK/STALE/ERROR verdict per held crew-warned quote to
// cr_crew_solver_log. This is a READ-ONLY review of the LATEST verdict per quote — nothing is
// auto-sent. The server dedupes by sentinel_quote_id (newest wins) within a created_at window and
// returns a `summary` verdict tally.
export function useTrsVerdicts({ days, limit } = {}) {
  const params = new URLSearchParams();
  if (days)  params.set('days', String(days));
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return useQuery({
    queryKey: ['trs', 'verdicts', days || null, limit || null],
    queryFn: () => apiFetch(`/api/trs/verdicts${qs ? `?${qs}` : ''}`),
    refetchInterval: 60_000,
  });
}

// S98: operator param — same trip_id exists for MULTIPLE operators (Avinode
// fan-out, H557YZ class). Without ?operator= the server returns the most
// recently created row, which is the WRONG operator on a collision.
export function useTripDetail(tripId, operator) {
  const qs = operator ? `?operator=${encodeURIComponent(operator)}` : '';
  return useQuery({
    queryKey: ['trip', tripId, operator || null],
    queryFn: () => apiFetch(`/api/trips/${encodeURIComponent(tripId)}${qs}`),
    enabled: !!tripId,
    refetchInterval: 30_000,
  });
}

// ─── mutations ───────────────────────────────────────────────────────────
// S98: every trip mutation carries `operator` so the server actions resolve the
// RIGHT operator's row on trip_id collisions (never the most-recent fallback).
export function useDeclineTrip() {
  return useMutation({
    mutationFn: ({ tripId, reason, force, operator }) =>
      apiFetch(`/api/trips/${encodeURIComponent(tripId)}/decline`, {
        method: 'POST',
        body: JSON.stringify({ reason, force, operator }),
      }),
  });
}

export function useApproveQuote() {
  return useMutation({
    mutationFn: ({ tripId, sentinelQuoteId, operator }) =>
      apiFetch(`/api/trips/${encodeURIComponent(tripId)}/approve`, {
        method: 'POST',
        body: JSON.stringify({ sentinel_quote_id: sentinelQuoteId, operator }),
      }),
  });
}

// S93 — manual-quote approval: point a held trip at a quote the operator created
// directly in Sentinel; the SFF validator reconciles it and Phase 4 posts to Avinode.
export function useManualQuote() {
  return useMutation({
    mutationFn: ({ tripId, sentinelQuoteId, price, tail, operator }) =>
      apiFetch(`/api/trips/${encodeURIComponent(tripId)}/use-quote`, {
        method: 'POST',
        body: JSON.stringify({ sentinel_quote_id: sentinelQuoteId, price, tail, operator }),
      }),
  });
}

export function useResetTrip() {
  return useMutation({
    mutationFn: ({ tripId, force, operator }) =>
      apiFetch(`/api/trips/${encodeURIComponent(tripId)}/reset`, {
        method: 'POST',
        body: JSON.stringify({ force, operator }),
      }),
  });
}

// Trigger Phase 4 to post an already-approved quote to Avinode immediately, bypassing
// the operating-hours gate (for after-hours approvals that would otherwise wait for the
// next in-window batch). Server guards on approved + validated + unsent + Avinode-source.
export function usePostNow() {
  return useMutation({
    mutationFn: ({ tripId, operator }) =>
      apiFetch(`/api/trips/${encodeURIComponent(tripId)}/post-now`, {
        method: 'POST',
        body: JSON.stringify({ operator }),
      }),
  });
}

// MANUAL "Send in Sentinel" (2026-07-11) — the CR-channel sibling of Post-now: sends a live
// CR-source quote natively in Sentinel. Keyed on the operator_quotes PK (globally unique —
// KI #55), not trip_id; server gates eligibility (CR-source + live) and the send is idempotent.
export function useSendSentinel() {
  return useMutation({
    mutationFn: ({ operatorQuoteId }) =>
      apiFetch(`/api/trips/quotes/${encodeURIComponent(operatorQuoteId)}/send-sentinel`, {
        method: 'POST',
      }),
  });
}

// "Use updated Sentinel quote" (2026-07-13) — the CR analog of Avinode's "Use this quote": the operator
// manually corrected a quote in Sentinel (e.g. a crew-clean re-quote under a NEW Sentinel #); send THAT
// updated quote to the customer, clear the original held quote, mark resolved. Keyed on the ORIGINAL
// operator_quotes PK (operatorQuoteId) + the new Sentinel quote number. Server (sendUpdatedCrQuote)
// re-gates CR-source + non-terminal + numeric-#.
export function useSendUpdatedQuote() {
  return useMutation({
    mutationFn: ({ operatorQuoteId, newSentinelQuoteId }) =>
      apiFetch(`/api/trips/quotes/${encodeURIComponent(operatorQuoteId)}/send-updated`, {
        method: 'POST',
        body: JSON.stringify({ new_sentinel_quote_id: newSentinelQuoteId }),
      }),
  });
}

// ─── bulk trip actions ───────────────────────────────────────────────────
// S98: bulk payloads send `trips: [{trip_id, operator}]` (operator-scoped) —
// the legacy trip_ids[] body resolved most-recent rows on trip_id collisions.
export function useBulkDeclineTrips() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trips, reason, force }) =>
      apiFetch('/api/trips/bulk/decline', {
        method: 'POST',
        body: JSON.stringify({ trips, reason, force }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trips'] }),
  });
}

export function useBulkResetTrips() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trips, force }) =>
      apiFetch('/api/trips/bulk/reset', {
        method: 'POST',
        body: JSON.stringify({ trips, force }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trips'] }),
  });
}

// ─── anomalies ───────────────────────────────────────────────────────────
export function useAnomalies({ since = '24h', event_type = null, acked = 'false', limit = 200 } = {}) {
  const params = new URLSearchParams();
  params.set('since', since);
  if (event_type) params.set('event_type', event_type);
  params.set('acked', acked);
  params.set('limit', String(limit));
  return useQuery({
    queryKey: ['anomalies', since, event_type, acked, limit],
    queryFn: () => apiFetch(`/api/anomalies?${params.toString()}`),
    refetchInterval: 30_000,
  });
}

// ─── config ──────────────────────────────────────────────────────────────
export function useConfigOperators() {
  return useQuery({
    queryKey: ['config', 'operators'],
    queryFn: () => apiFetch('/api/config/operators'),
    staleTime: 60_000,
  });
}

export function useConfigOperator(operatorId) {
  return useQuery({
    queryKey: ['config', 'operator', operatorId],
    queryFn: () => apiFetch(`/api/config/operators/${encodeURIComponent(operatorId)}`),
    enabled: !!operatorId,
    staleTime: 60_000,
  });
}

// ─── operator quoting pause ────────────────────────────────────────────────
export function useOperatorPauseStatus(operatorId) {
  return useQuery({
    queryKey: ['operator', 'pause-status', operatorId],
    queryFn: () => apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/pause-status`),
    enabled: !!operatorId,
    refetchInterval: 30_000,
  });
}

export function usePauseOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, reason }) =>
      apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/pause`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['operator', 'pause-status', vars.operatorId] }),
  });
}

export function useResumeOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, reason }) =>
      apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/resume`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['operator', 'pause-status', vars.operatorId] }),
  });
}

// ─── operator quoting hours (SQP-332) ───────────────────────────────────────
export function useOperatorQuotingHours(operatorId) {
  return useQuery({
    queryKey: ['operator', 'quoting-hours', operatorId],
    queryFn: () => apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/quoting-hours`),
    enabled: !!operatorId,
    refetchInterval: 30_000,
  });
}

// body: { tz, start, end } to set, or { clear: true } to remove the limit (24/7).
export function useSetOperatorQuotingHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, body }) =>
      apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/quoting-hours`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['operator', 'quoting-hours', vars.operatorId] });
      qc.invalidateQueries({ queryKey: ['system', 'cr-drain-health'] });
    },
  });
}

// ─── request blacklist (migration 029) ─────────────────────────────────────
export function useBlacklistRules(operatorId) {
  return useQuery({
    queryKey: ['operator', 'blacklist', operatorId],
    queryFn: () => apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/blacklist`),
    enabled: !!operatorId,
    refetchInterval: 60_000,
  });
}

export function useAddBlacklistRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, ...body }) =>
      apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/blacklist`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['operator', 'blacklist', vars.operatorId] }),
  });
}

export function useRemoveBlacklistRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, ruleId }) =>
      apiFetch(`/api/operators/${encodeURIComponent(operatorId)}/blacklist/${encodeURIComponent(ruleId)}`, {
        method: 'DELETE',
      }),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['operator', 'blacklist', vars.operatorId] }),
  });
}

// ─── batches ─────────────────────────────────────────────────────────────
export function useBatches({ operator = null, since = '24h', limit = 500 } = {}) {
  const params = new URLSearchParams();
  if (operator) params.set('operator', operator);
  params.set('since', since);
  params.set('limit', String(limit));
  return useQuery({
    queryKey: ['batches', operator, since, limit],
    queryFn: () => apiFetch(`/api/batches?${params.toString()}`),
    refetchInterval: 60_000,
  });
}

export function useAckAnomaly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowid, unack = false }) =>
      apiFetch(`/api/anomalies/${rowid}/ack`, {
        method: 'POST',
        body: JSON.stringify({ unack }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anomalies'] }),
  });
}

export function useAckAnomaliesBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowids, unack = false }) =>
      apiFetch('/api/anomalies/bulk/ack', {
        method: 'POST',
        body: JSON.stringify({ rowids, unack }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anomalies'] }),
  });
}
